/* @jsxImportSource react */
/* Vite's react plugin already compiles JSX with react/jsx-runtime; the pragma keeps
   that true under Playwright's babel transform too, so the node-side unit tests
   (tests/unit/*.unit.spec.tsx) can render this file without a browser. */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';

import { jobs } from '../api/client';
import type { JobDetailView } from '../api/types';
import { formatAge, formatBytes, formatDuration, formatTs, shortSha, shortenPath } from '../lib/format';
import { Badge, ErrorState, Spinner, StatusPill } from '../components/ui';
import { ZONE_META, ZoneBadge } from '../components/ZoneBadge';

/** The "Run now" button (SPEC §8): RU via the runner queue, KZ in-cluster. */
export function RunNowButton({ job }: { job: JobDetailView }) {
  const [message, setMessage] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // The tooltip explains why the button cannot be pressed — that IS the UX.
  // Only two states stay unpressable: the flag off, and jobs without a CronJob.
  const tooltip = !job.run.enabled
    ? 'ENABLE_RUN_BACKUP=false'
    : !job.run.supported
      ? (job.run.reason ?? 'run недоступен')
      : `Запустить ${job.runmech ? `${job.runmech.cronjob} (${job.runmech.ns})` : ''} сейчас`;

  async function onPress(): Promise<void> {
    if (!job.run.enabled || !job.run.supported) return;
    setPending(true);
    setMessage(null);
    try {
      const result = await jobs.runNow(job.id);
      const warnings = result.warnings.length > 0 ? ` ${result.warnings.join(' ')}` : '';
      setMessage(
        // KZ (state `created`): the Job already exists in ns `backup`, the run
        // shows up in the timeline as soon as its first event arrives. RU: the
        // command still has to be picked up by the runner agent.
        result.state === 'created'
          ? `Job ${result.commandId} создан в кластере (ns backup).${warnings}`
          : `Команда ${result.commandId} в очереди (runner заберёт её в течение 25 с).${warnings}`,
      );
    } catch (error) {
      setMessage(String(error instanceof Error ? error.message : error));
    } finally {
      setPending(false);
    }
  }

  const disabled = !job.run.enabled || !job.run.supported;
  return (
    <div>
      <button
        type="button"
        className="btn btn--primary"
        disabled={disabled || pending}
        title={tooltip}
        aria-label="Run backup now"
        data-testid="run-now"
        onClick={() => void onPress()}
      >
        Run now
      </button>
      {disabled ? (
        <span className="muted" style={{ marginLeft: 'var(--space-2)', fontSize: 12 }}>
          {tooltip}
        </span>
      ) : null}
      {message ? (
        <p className="field__hint" role="status">
          {message}
        </p>
      ) : null}
    </div>
  );
}

/** Where the job is driven from, phrased per zone when there is no CronJob. */
function runMechLabel(job: JobDetailView): string {
  if (job.runmech) return `${job.runmech.cluster}/${job.runmech.ns}/${job.runmech.cronjob}`;
  if (job.zone === 'gh') return 'ручная — GitHub Actions';
  if (job.zone === 'laptop') return 'ручная — ноутбук оператора';
  if (job.zone === 'kz-vm') return 'ручная/VM — cron на самой ВМ';
  return 'ручная/VM/GH — CronJob нет';
}

/** Retention: job-level keep + targets that override it. */
export function RetentionFacts({ job }: { job: JobDetailView }) {
  const overrides = job.targets_spec.filter((target) => target.keep != null && target.keep !== job.keep);
  return (
    <>
      {job.keep === null ? '—' : `keep ${job.keep}`}
      {overrides.length > 0 ? (
        <span className="muted">
          {' '}
          · override: {overrides.map((target) => `${target.name} ${target.keep}`).join(', ')}
        </span>
      ) : null}
    </>
  );
}

/** Registry facts of one job (the "what is this" half of the page). */
export function JobFacts({ job }: { job: JobDetailView }) {
  return (
    <dl className="meta" data-testid="job-facts">
      <dt>id</dt>
      <dd>{job.id}</dd>
      <dt>Зона</dt>
      <dd>
        <ZoneBadge zone={job.zone} />
        <span className="muted"> {ZONE_META[job.zone]?.title.replace('зона исполнения: ', '') ?? job.zone}</span>
      </dd>
      <dt>Где</dt>
      <dd>{job.where}</dd>
      <dt>Cron</dt>
      <dd>
        {job.cron ?? '—'} <span className="muted">({job.timezone})</span>
      </dd>
      <dt>Движок</dt>
      <dd>
        {job.engine}/{job.btype} · RPO {job.rpoHours} ч
      </dd>
      <dt>Бакет</dt>
      <dd>
        {job.sweep.bucket === null ? (
          '— (нет S3-артефакта)'
        ) : (
          <>
            {job.sweep.bucket} <span className="muted">· {job.sweep.endpoint === 'kz' ? 'KZ endpoint' : 'RU endpoint'}</span>
          </>
        )}
      </dd>
      <dt>Ретеншн</dt>
      <dd>
        <RetentionFacts job={job} />
      </dd>
      <dt>Базы данных</dt>
      <dd data-testid="job-databases">
        {(() => {
          const dbs = (job.targets_spec ?? []).flatMap((target) => target.dbs ?? []);
          return dbs.length > 0 ? (
            <>
              <strong>{dbs.length}</strong>
              <span className="chip-row" style={{ marginLeft: '0.4rem', display: 'inline-flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                {dbs.map((db) => (
                  <Badge key={db} tone="neutral">
                    {db}
                  </Badge>
                ))}
              </span>
            </>
          ) : (
            <span className="muted">— (нет перечня: file-артефакт или MDB-native)</span>
          );
        })()}
      </dd>
      <dt>Runner</dt>
      <dd>
        {runMechLabel(job)}
        {job.instrumented ? <Badge tone="ok">instrumented</Badge> : <Badge tone="warn">no events yet</Badge>}
      </dd>
    </dl>
  );
}

/** Targets the job is expected to produce every run. */
export function TargetsTable({ job }: { job: JobDetailView }) {
  return (
    <div className="table-wrap">
      <table className="data" data-testid="targets-table">
        <thead>
          <tr>
            <th>Таргет</th>
            <th>Статус</th>
            <th className="num">Возраст</th>
            <th className="num">Размер</th>
            <th>KZ</th>
            <th>sha256</th>
            <th>Путь</th>
            <th className="num">Прошлый прогон</th>
          </tr>
        </thead>
        <tbody>
          {job.targets.map((target) => (
            <tr key={target.name}>
              <td className="cell-primary" title={target.name}>
                <span className={`dot dot--${target.status}`} aria-hidden="true" /> {target.name}
                {/* K4: show the target's own project only where it departs from the job's. */}
                {target.project && target.project !== job.project ? (
                  <Badge tone="neutral" title="проект этого таргета (K4: проект принадлежит таргету)">
                    {target.project}
                  </Badge>
                ) : null}
                {(() => {
                  const spec = job.targets_spec.find((entry) => entry.name === target.name);
                  if (!spec?.dbs?.length) return null;
                  return (
                    <span style={{ display: 'block', marginTop: '0.15rem' }} className="muted">
                      БД: {spec.dbs.join(', ')}
                    </span>
                  );
                })()}
              </td>
              <td>
                <StatusPill status={target.status} />
              </td>
              <td className="num cell-muted">{formatAge(target.age_s)}</td>
              <td className="num cell-muted">{formatBytes(target.size_bytes)}</td>
              <td>
                {target.kz === null ? (
                  <span className="dimmed">—</span>
                ) : (
                  <Badge tone={target.kz === 'ok' ? 'ok' : target.kz === 'fail' ? 'bad' : 'neutral'} title={`dual-write KZ: ${target.kz}`}>
                    {target.kz}
                  </Badge>
                )}
              </td>
              <td className="cell-muted" title={target.sha256 ?? ''}>
                {shortSha(target.sha256)}
              </td>
              <td className="cell-muted" title={target.path ?? ''}>
                {shortenPath(target.path)}
              </td>
              <td className="num cell-muted">
                {target.last_run_ts ? (
                  <>
                    {formatTs(target.last_run_ts)}{' '}
                    {target.last_run_status ? <StatusPill status={target.last_run_status} /> : null}
                  </>
                ) : (
                  '—'
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** `/jobs/:id` — registry facts, targets, planned, run list, run button. */
export function JobDetailPage() {
  const { id = '' } = useParams();
  const query = useQuery({
    queryKey: ['job', id],
    queryFn: ({ signal }) => jobs.get(id, signal),
    refetchInterval: 30_000,
    placeholderData: (previous) => previous,
  });

  if (query.isPending) return <Spinner label={`Загружаю джобу ${id}`} />;
  if (query.isError) return <ErrorState message={String(query.error)} onRetry={() => void query.refetch()} />;
  if (!query.data) return <ErrorState message={`Джоба «${id}» не найдена в реестре`} />;

  const job = query.data;
  return (
    <>
      <div className="page-header">
        <div>
          <h1>
            {job.name} <ZoneBadge zone={job.zone} />
          </h1>
          <p className="page-header__sub">
            <Link to="/">← все джобы</Link> · <StatusPill status={job.status.status} title={job.status.reason} />{' '}
            <span className="muted">{job.status.reason}</span>
          </p>
        </div>
        <RunNowButton job={job} />
      </div>

      <section className="card">
        <h2 className="section-title">Реестр</h2>
        <JobFacts job={job} />
      </section>

      <section className="card card--flush">
        <h2 className="section-title" style={{ padding: 'var(--space-4) var(--space-5) 0' }}>
          Таргеты ({job.targets.length})
        </h2>
        <TargetsTable job={job} />
      </section>

      {job.planned.length > 0 ? (
        <section className="card">
          <h2 className="section-title">Planned (не бэкапятся ночью)</h2>
          <p className="dimmed" data-testid="planned-targets">
            {job.planned.join(', ')}
          </p>
        </section>
      ) : null}

      <section className="card card--flush">
        <h2 className="section-title" style={{ padding: 'var(--space-4) var(--space-5) 0' }}>
          Прогоны (последние {job.runs.length})
        </h2>
        {job.runs.length === 0 ? (
          <p className="dimmed" style={{ padding: '0 var(--space-5) var(--space-4)' }}>
            Прогонов ещё не было — коллектор не получал событий от этой джобы.
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data" data-testid="runs-table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Старт</th>
                  <th className="num">Длительность</th>
                  <th>Статус</th>
                  <th className="num">Uploaded</th>
                  <th className="num">Skipped</th>
                  <th className="num">Failed</th>
                  <th className="num">Deleted</th>
                </tr>
              </thead>
              <tbody>
                {job.runs.map((run) => (
                  <tr key={run.run}>
                    <td className="cell-primary">
                      <Link to={`/jobs/${job.id}/runs/${run.run}`}>{run.run}</Link>
                    </td>
                    <td className="cell-muted">{formatTs(run.started)}</td>
                    <td className="num cell-muted">{formatDuration(run.duration_s)}</td>
                    <td>
                      <StatusPill status={run.status} title={run.headless ? 'headless: события без run_start' : undefined} />
                    </td>
                    <td className="num">{run.uploaded ?? '—'}</td>
                    <td className="num">{run.skipped ?? '—'}</td>
                    <td className="num">{run.failed ?? '—'}</td>
                    <td className="num">{run.deleted ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
